import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material'
import { postSPDataWithParams } from 'src/supportPortal/services/services'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  getPriceStringWithCurrency,
} from 'src/mentor/helpers/utilityFunctions'
import { isValidNumber } from 'src/consumer/helpers/utilFunctions'
import { ModelViewDialogPaper } from './feeModelStyledComponents'

function getExistingReconciliationAdjustment(report) {
  if (report?.reconciliationAdjustment) {
    return report.reconciliationAdjustment
  }
  if (!report?.reconciliationApplied) {
    return null
  }
  return {
    correctedFeeCollected: report?.feeCollected,
    adjustmentAmount: report?.reconciliationAmount,
    note: report?.reconciliationReason,
    appliedAt: report?.reconciliationAppliedAt,
    appliedBy: report?.reconciliationAppliedBy,
  }
}

function getReconciliationApiSignature(isCorrection) {
  const action = isCorrection ? 'correct' : 'apply'
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname
    if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
      return `/api/secure/cc/support/payout-reports/reconciliation/${action}`
    }
  }
  return `/api/secure/support/payout-reports/reconciliation/${action}`
}

export default function ReconciliationDialog({
  open,
  onClose,
  report,
  onSuccess,
  renderInDrawer = false,
}) {
  const theme = useTheme()
  const [correctedFeeCollected, setCorrectedFeeCollected] = useState('')
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const existingAdjustment = getExistingReconciliationAdjustment(report)
  const isCorrection = !!existingAdjustment

  // Calculate suggested fee (2% deduction from feeCollectedFromPatient)
  const suggestedFee = report?.feeCollectedFromPatient
    ? report.feeCollectedFromPatient * 0.98
    : null

  const handleClose = () => {
    if (!loading) {
      setCorrectedFeeCollected('')
      setNote('')
      setError(null)
      onClose()
    }
  }

  const handleApplySuggestion = () => {
    if (suggestedFee) {
      setCorrectedFeeCollected(suggestedFee.toFixed(2))
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }
    setCorrectedFeeCollected(
      isValidNumber(existingAdjustment?.correctedFeeCollected)
        ? String(existingAdjustment.correctedFeeCollected)
        : '',
    )
    setNote('')
    setError(null)
  }, [open, report?.bookedEventId, existingAdjustment?.correctedFeeCollected])

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    setError(null)

    const bookedEventId = report?.bookedEventId || report?.id

    if (!bookedEventId) {
      setError('Booked Event ID is missing')
      return
    }

    if (!correctedFeeCollected || correctedFeeCollected.trim() === '') {
      setError('Please enter a valid corrected fee amount')
      return
    }

    const feeValue = parseFloat(correctedFeeCollected)

    if (isNaN(feeValue) || !isFinite(feeValue) || feeValue < 0) {
      setError('Corrected fee must be a valid positive number')
      return
    }

    if (!isValidNumber(feeValue)) {
      setError('Please enter a valid corrected fee amount')
      return
    }

    if (isCorrection && (!note || note.trim() === '')) {
      setError('Please add a note explaining this correction')
      return
    }

    if (loading) {
      return
    }

    setLoading(true)

    try {
      const requestData = {
        bookedEventId: bookedEventId,
        correctedFeeCollected: feeValue,
        note: note || undefined,
      }

      const response = await postSPDataWithParams(
        getReconciliationApiSignature(isCorrection),
        requestData,
      )

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const data = getDataMapFromCommonResponse(response)
        toast.success(
          isCorrection
            ? 'Reconciliation correction saved successfully'
            : 'Reconciliation applied successfully',
        )
        if (onSuccess) {
          onSuccess(data?.['booked-event-info'])
        }
        handleClose()
      } else {
        const errorMessage =
          response?.data?.message ||
          (isCorrection ? 'Failed to correct reconciliation' : 'Failed to apply reconciliation')
        setError(errorMessage)
        toast.error(errorMessage)
      }
    } catch (err) {
      console.error('Error saving reconciliation:', err)
      const errorMessage =
        err?.response?.data?.message ||
        (isCorrection ? 'Error correcting reconciliation' : 'Error applying reconciliation')
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const formContent = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        marginTop: renderInDrawer ? 0 : theme.spacing(1),
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {isCorrection && (
        <Alert severity="warning">
          This booking is already reconciled. Saving here will replace the current corrected amount
          and keep the previous reconciliation in the audit trail.
        </Alert>
      )}

      <div>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Current Fee Collected
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {report?.feeCollected
            ? getPriceStringWithCurrency(report.feeCollected, report?.currency)
            : 'No Data'}
        </Typography>
      </div>

      <div>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Fee for this session
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {report?.feeCollectedFromPatient
            ? getPriceStringWithCurrency(report.feeCollectedFromPatient, report?.currency)
            : 'No Data'}
        </Typography>
      </div>

      {suggestedFee && (
        <div
          style={{
            padding: theme.spacing(2),
            backgroundColor: theme.palette.info.light,
            borderRadius: theme.shape.borderRadius,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Typography variant="body2" color="text.secondary">
              Suggested (2% deduction):
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {getPriceStringWithCurrency(suggestedFee, report?.currency)}
            </Typography>
          </div>
          <Button
            size="small"
            variant="outlined"
            onClick={handleApplySuggestion}
            disabled={loading}
          >
            Apply
          </Button>
        </div>
      )}

      <TextField
        label={isCorrection ? 'New corrected fee collected' : 'Corrected Fee Collected'}
        type="number"
        value={correctedFeeCollected}
        onChange={(e) => setCorrectedFeeCollected(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            if (correctedFeeCollected && !loading) {
              handleSubmit()
            }
          }
        }}
        fullWidth
        required
        disabled={loading}
        inputProps={{ min: 0, step: 0.01 }}
        helperText={
          isCorrection
            ? 'Enter the corrected net fee that should replace the current reconciliation'
            : 'Enter the net fee after reconciliation adjustments'
        }
      />

      <TextField
        label={isCorrection ? 'Correction note' : 'Note (Optional)'}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        multiline
        rows={3}
        disabled={loading}
        placeholder="e.g., Apply 2% cash collection deduction + Rs. 5 rounding adjustment"
        helperText={
          isCorrection
            ? 'Required for corrections and stored in the event audit trail'
            : 'This note will be stored in the event modification log for auditing'
        }
      />
    </div>
  )

  const actionsContent = (
    <div
      style={{
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        flexWrap: 'wrap',
      }}
    >
      <Button onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={(e) => {
          if (e) {
            e.preventDefault()
            e.stopPropagation()
          }
          handleSubmit(e).catch((err) => console.error('Error in handleSubmit:', err))
        }}
        variant="contained"
        disabled={loading || !correctedFeeCollected}
        type="button"
        sx={{ minWidth: '150px', position: 'relative', zIndex: 1 }}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : isCorrection ? (
          'Save Correction'
        ) : (
          'Apply Reconciliation'
        )}
      </Button>
    </div>
  )

  if (renderInDrawer) {
    return (
      <div>
        {formContent}
        {actionsContent}
      </div>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' && loading) return
        handleClose()
      }}
      PaperComponent={ModelViewDialogPaper}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{isCorrection ? 'Correct Reconciliation' : 'Apply Reconciliation'}</DialogTitle>
      <DialogContent>{formContent}</DialogContent>
      <DialogActions sx={{ gap: 1 }}>{actionsContent}</DialogActions>
    </Dialog>
  )
}
